/**
 * ──────────────────────────────────────────────────────────────────────────
 *  CONTEXT-LOCKED PALETTE — Comms Degradation Zones (Ice Storm Risk →
 *  "Estimated Comms Degradation"; operator-cop-doctrine §3A.7 context-locking,
 *  §3B.8 the AMBIENT register, §2.1 comms/reach-back, IMS §2 resilient telecoms).
 *
 *  The SINGLE source of the comms-degradation-zone palette. Consumed ONLY by the
 *  comms family:
 *    - CommsDegradationZonesLayer (the translucent ground-clamped zone polygon)
 *    - CommsDegradationZoneBody (the side-panel details + confidence badge)
 *
 *  Per §3B.8: SHAPE encodes "an ESTIMATED comms-loss zone" (a translucent,
 *  ground-clamped MultiPolygon envelope — never a flame, dot, or dashed line);
 *  COLOUR encodes the estimate CONFIDENCE within the family (battery-decay +
 *  corroboration); MOTION is NOT in this family's budget (estimate zones are
 *  ambient, never pulse). Colour is never load-bearing alone — every render pairs
 *  the hue with the word "estimated" + the confidence label.
 *
 *  Confidence → hue (the comms family's OWN indigo tokens, deliberately distinct
 *  from outage red / road bronze-orange / flood fuchsia / fire flame / anomaly red
 *  so the estimate reads apart from every measured signal):
 *    possible   faint indigo   #a5b4fc   (<2 h — towers likely still on battery)
 *    probable   indigo         #818cf8   (2–8 h — batteries draining)
 *    likely     deep indigo    #6366f1   (≥8 h, or corroborated)
 *    unknown    neutral grey   #9ca3af   (unresolved confidence)
 *
 *  ⛔ Do NOT import from another colour family (./responseLevel, ./outageStyles,
 *     ./floodGauges, ./roadClosureStyles, …) — enforced by the no-restricted-imports
 *     lint rule in eslint.config.js. Pair colour with the confidence label instead.
 * ──────────────────────────────────────────────────────────────────────────
 */

export type CommsConfidence = 'possible' | 'probable' | 'likely';

/** Confidence-band hues — the single source of truth. */
export const COMMS_CONFIDENCE_COLORS: Record<CommsConfidence, string> = {
  possible: '#a5b4fc',
  probable: '#818cf8',
  likely: '#6366f1',
};

/** Neutral grey when the confidence can't be resolved (never dropped). */
export const COMMS_DEFAULT_COLOR = '#9ca3af';

/** Operator-facing confidence label, paired with the colour so hue is never alone. */
export const COMMS_CONFIDENCE_LABEL: Record<CommsConfidence, string> = {
  possible: 'Possible (est.)',
  probable: 'Probable (est.)',
  likely: 'Likely (est.)',
};

export interface CommsZoneStyle {
  /** CSS hex for the polygon fill + outline + badge. */
  color: string;
  /** Confidence label — colour is never load-bearing alone. */
  label: string;
}

/** Resolve the render style for a zone's estimate confidence. Tolerant of null. */
export function commsZoneStyle(confidence: string | null | undefined): CommsZoneStyle {
  if (confidence === 'possible' || confidence === 'probable' || confidence === 'likely') {
    return { color: COMMS_CONFIDENCE_COLORS[confidence], label: COMMS_CONFIDENCE_LABEL[confidence] };
  }
  return { color: COMMS_DEFAULT_COLOR, label: 'Estimated' };
}

/** Fill opacity for the ambient, ground-clamped estimate-zone polygon — faint
 *  (it is an estimate; it must not dominate measured signals beneath it). */
export const COMMS_ZONE_FILL_ALPHA = 0.18;

/** Outline opacity + width (px) for the zone boundary. */
export const COMMS_ZONE_OUTLINE_ALPHA = 0.55;
export const COMMS_ZONE_OUTLINE_WIDTH = 2;
